import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Loader2,
  AlertCircle,
  Maximize,
  Minimize,
  RefreshCw,
  Lock,
  Link2Off,
} from 'lucide-react';
import DashboardBlockCard from '@/components/dashboard-builder/DashboardBlockCard';
import ControlBar from '@/components/dashboard-builder/controls/ControlBar';
import { useSharedDashboard } from '@/hooks/useSharedDashboard';
import { useRelativeTime } from '@/hooks/useRelativeTime';
import { useIdleTimer } from '@/hooks/useIdleTimer';
import { DASHBOARD_TOKENS } from '@/components/dashboard-builder/rendering/tokens';
import type { RenderHandle } from '@/types/dashboard';

const SharedDashboardPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [searchParams] = useSearchParams();
  const isKiosk = searchParams.get('kiosk') === 'true';

  const {
    phase,
    dashboard,
    blocks,
    blockData,
    controls,
    activeControlValues,
    isRefreshing,
    error,
    pinError,
    renderHandles,
    submitPin,
    changeControlValue,
    refreshData,
    lastRefreshedAt,
    isPolling,
  } = useSharedDashboard(shareToken);

  const { isIdle } = useIdleTimer(isKiosk ? 2000 : 5000);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [isSubmittingPin, setIsSubmittingPin] = useState(false);
  const [kioskActivated, setKioskActivated] = useState(false);

  const relativeTime = useRelativeTime(lastRefreshedAt);
  const isLive = dashboard?.data_mode === 'live';

  const showControls = dashboard?.share_settings?.show_controls !== false;
  const visibleControls = useMemo(
    () => (showControls ? controls : []),
    [showControls, controls]
  );

  const handleRenderHandle = useCallback(
    (handle: RenderHandle) => {
      renderHandles.current.set(handle.blockId, handle);
    },
    [renderHandles]
  );

  const handleDisposeHandle = useCallback(
    (blockId: string) => {
      renderHandles.current.delete(blockId);
    },
    [renderHandles]
  );

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      // Fullscreen may not be available
    }
  }, []);

  // Sync fullscreen state
  useEffect(() => {
    const onFullscreenChange = (): void => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Kiosk: auto-fullscreen on first user interaction
  useEffect(() => {
    if (!isKiosk || kioskActivated || phase !== 'ready') return;

    const onInteraction = async (): Promise<void> => {
      setKioskActivated(true);
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        // Ignore
      }
      document.removeEventListener('click', onInteraction);
      document.removeEventListener('keydown', onInteraction);
    };

    document.addEventListener('click', onInteraction, { once: true });
    document.addEventListener('keydown', onInteraction, { once: true });

    return () => {
      document.removeEventListener('click', onInteraction);
      document.removeEventListener('keydown', onInteraction);
    };
  }, [isKiosk, kioskActivated, phase]);

  // Keyboard: F to toggle fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'f' || e.key === 'F') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        toggleFullscreen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);

  const handlePinSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (pinInput.length < 4) return;
    setIsSubmittingPin(true);
    try {
      await submitPin(pinInput);
    } finally {
      setIsSubmittingPin(false);
    }
  };

  // ---------- Loading ----------
  if (phase === 'loading') {
    return (
      <div
        className="dark flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="text-center">
          <Loader2
            className="w-10 h-10 animate-spin mx-auto mb-3"
            style={{ color: DASHBOARD_TOKENS.textSecondary }}
          />
          <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm">
            Loading shared dashboard...
          </p>
        </div>
      </div>
    );
  }

  // ---------- Expired ----------
  if (phase === 'expired') {
    return (
      <div
        className="dark flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="text-center max-w-md px-6">
          <Link2Off
            className="w-14 h-14 mx-auto mb-5"
            style={{ color: DASHBOARD_TOKENS.textMuted }}
          />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: DASHBOARD_TOKENS.textPrimary }}
          >
            Link expired
          </h2>
          <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm">
            This shared dashboard link has expired. Please contact the dashboard owner for a new link.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (phase === 'error') {
    return (
      <div
        className="dark flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-14 h-14 text-destructive mx-auto mb-5" />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: DASHBOARD_TOKENS.textPrimary }}
          >
            Dashboard unavailable
          </h2>
          <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm">
            {error || 'This dashboard is no longer available. The link may have been revoked.'}
          </p>
        </div>
      </div>
    );
  }

  // ---------- PIN required ----------
  if (phase === 'pin_required') {
    return (
      <div
        className="dark flex items-center justify-center h-screen"
        style={{ backgroundColor: DASHBOARD_TOKENS.bgPrimary }}
      >
        <div className="w-full max-w-sm px-6">
          <div className="text-center mb-8">
            <Lock
              className="w-12 h-12 mx-auto mb-4"
              style={{ color: DASHBOARD_TOKENS.textSecondary }}
            />
            <h2
              className="text-lg font-semibold mb-1"
              style={{ color: DASHBOARD_TOKENS.textPrimary }}
            >
              PIN required
            </h2>
            <p style={{ color: DASHBOARD_TOKENS.textMuted }} className="text-sm">
              Enter the PIN to view this dashboard
            </p>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                autoFocus
                value={pinInput}
                onChange={e => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter PIN"
                className="w-full text-center text-2xl tracking-[0.3em] py-3 rounded-lg border bg-white/5 focus:outline-none focus:ring-2 focus:ring-ring"
                style={{
                  borderColor: pinError ? '#ef4444' : DASHBOARD_TOKENS.borderDefault,
                  color: DASHBOARD_TOKENS.textPrimary,
                }}
              />
              {pinError && (
                <p className="mt-2 text-sm text-destructive text-center">{pinError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={pinInput.length < 4 || isSubmittingPin}
              className="w-full py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmittingPin ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              {isSubmittingPin ? 'Verifying...' : 'Submit'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs" style={{ color: DASHBOARD_TOKENS.textMuted }}>
            Powered by Zamino
          </p>
        </div>
      </div>
    );
  }

  // ---------- Ready: dashboard visible ----------
  const layoutColumns = dashboard?.layout_columns ?? 2;
  const hasBlocks = blocks.length > 0;
  const hideTopBar = isKiosk;

  return (
    <div
      className="dark flex flex-col h-screen overflow-hidden select-none"
      style={{
        backgroundColor: DASHBOARD_TOKENS.bgPrimary,
        cursor: isIdle ? 'none' : 'default',
      }}
    >
      {/* Top bar (hidden in kiosk mode, auto-hides on idle otherwise) */}
      {!hideTopBar && (
        <div
          className={clsx(
            'flex-shrink-0 px-5 py-2.5 flex items-center gap-3 transition-opacity duration-500',
            isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
          )}
          style={{ backgroundColor: DASHBOARD_TOKENS.bgHeader }}
        >
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <h1
              className="text-sm font-medium truncate"
              style={{ color: DASHBOARD_TOKENS.textSecondary }}
            >
              {dashboard?.name}
            </h1>

            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-primary/10 text-primary border border-primary/20 flex-shrink-0">
              Shared
            </span>

            {isLive && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-success/10 text-success border border-success/20 flex-shrink-0">
                <span
                  className={clsx(
                    'w-1.5 h-1.5 rounded-full bg-success',
                    isPolling && 'animate-pulse'
                  )}
                />
                Live
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5 flex-shrink-0">
            {relativeTime && (
              <span
                className="text-[11px] tabular-nums"
                style={{ color: DASHBOARD_TOKENS.textMuted }}
              >
                Updated {relativeTime}
              </span>
            )}

            <button
              onClick={refreshData}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors hover:bg-white/5 disabled:opacity-40"
              style={{ color: DASHBOARD_TOKENS.textSecondary }}
              title="Refresh data"
            >
              <RefreshCw
                className={clsx('w-3.5 h-3.5', isRefreshing && 'animate-spin')}
              />
            </button>
          </div>
        </div>
      )}

      {/* Control bar */}
      {visibleControls.length > 0 && (
        <div
          className={clsx(
            'transition-opacity duration-500',
            !hideTopBar && isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
          )}
        >
          <ControlBar
            controls={visibleControls}
            activeControlValues={activeControlValues}
            isRefreshing={isRefreshing}
            onChangeControlValue={changeControlValue}
          />
        </div>
      )}

      {/* Block grid */}
      <div className="flex-1 overflow-y-auto">
        {hasBlocks ? (
          <div
            className="p-4 gap-4"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${layoutColumns}, 1fr)`,
              alignContent: 'start',
            }}
          >
            {blocks.map((block) => {
              const data = blockData.get(block.id) ?? [];
              const spanW = Math.min(block.position.w, layoutColumns);

              return (
                <div key={block.id} style={{ gridColumn: `span ${spanW}` }}>
                  <DashboardBlockCard
                    block={block}
                    data={data}
                    viewOnly
                    onRenderHandle={handleRenderHandle}
                    onDisposeHandle={handleDisposeHandle}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-sm px-4">
              <AlertCircle
                className="w-12 h-12 mx-auto mb-4"
                style={{ color: DASHBOARD_TOKENS.textMuted }}
              />
              <h3
                className="text-lg font-medium mb-2"
                style={{ color: DASHBOARD_TOKENS.textSecondary }}
              >
                No charts to display
              </h3>
              <p
                className="text-sm"
                style={{ color: DASHBOARD_TOKENS.textMuted }}
              >
                This dashboard doesn't have any charts yet.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom-right overlay: fullscreen toggle (hidden in kiosk) */}
      {!hideTopBar && (
        <div
          className={clsx(
            'fixed bottom-4 right-4 flex items-center gap-2 transition-opacity duration-500',
            isIdle ? 'opacity-0 pointer-events-none' : 'opacity-100'
          )}
        >
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-lg transition-colors hover:bg-white/10"
            style={{ color: DASHBOARD_TOKENS.textSecondary }}
            title={isFullscreen ? 'Exit fullscreen (F)' : 'Enter fullscreen (F)'}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4" />
            ) : (
              <Maximize className="w-4 h-4" />
            )}
          </button>
        </div>
      )}

      {/* Kiosk: subtle "Powered by Zamino" watermark, bottom-left */}
      {isKiosk && (
        <div
          className={clsx(
            'fixed bottom-2 left-3 transition-opacity duration-500',
            isIdle ? 'opacity-0' : 'opacity-30'
          )}
        >
          <span className="text-[10px]" style={{ color: DASHBOARD_TOKENS.textMuted }}>
            Powered by Zamino
          </span>
        </div>
      )}
    </div>
  );
};

export default SharedDashboardPage;
